import {Logger} from '@/util/logger';

import {EasingType} from './easing';
import {EasingLookupTable} from './easing.lookup.table';

/**
 *
 * @class Zoomer
 *
 * This is the base class/interface for the adapters that manage zooming in/out for us.
 * The PanZoom controller is already doing "kinetic" (momentum based) panning, but for
 * zooming in/out a different model may be needed, or we may want to try different models
 * and allow the user to switch to one they prefer.  This base class lets us have a common
 * interface point, so we the PanZoom control can just use which ever kind of zoom model
 * the user prefers.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */

export class Zoomer {

  /** the easing mode */
  public easing: EasingType                                 = EasingType.OUT_QUADRATIC;

  /** the possible easing modes */
  public easingModes: EasingType[] = [
    EasingType.LINEAR,           EasingType.IN_QUADRATIC,     EasingType.IN_CUBIC,       EasingType.IN_QUARTIC,     EasingType.IN_QUINTIC,
    EasingType.IN_SEXTIC,        EasingType.IN_SEPTIC,        EasingType.IN_OCTIC,       EasingType.OUT_QUADRATIC,  EasingType.OUT_CUBIC,
    EasingType.OUT_QUARTIC,      EasingType.OUT_QUINTIC,      EasingType.OUT_SEXTIC,     EasingType.OUT_SEPTIC,     EasingType.OUT_OCTIC,
    EasingType.IN_OUT_QUADRATIC, EasingType.IN_OUT_CUBIC,     EasingType.IN_OUT_QUARTIC, EasingType.IN_OUT_QUINTIC, EasingType.IN_OUT_SEXTIC,
    EasingType.IN_OUT_SEPTIC,    EasingType.IN_OUT_OCTIC,     EasingType.IN_BACK,        EasingType.IN_OUT_BACK,    EasingType.OUT_BACK,
    EasingType.IN_BOUNCE,        EasingType.IN_OUT_BOUNCE,    EasingType.OUT_BOUNCE,     EasingType.IN_CIRCLE,      EasingType.IN_OUT_CIRCLE,
    EasingType.OUT_CIRCLE,       EasingType.IN_ELASTIC,       EasingType.IN_OUT_ELASTIC, EasingType.OUT_ELASTIC,    EasingType.IN_EXPONENT2,
    EasingType.IN_OUT_EXPONENT2, EasingType.OUT_EXPONENT2,    EasingType.IN_SINE,        EasingType.IN_OUT_SINE,    EasingType.OUT_SINE,
    EasingType.IN_EXPONENTE,     EasingType.IN_OUT_EXPONENTE, EasingType.OUT_EXPONENTE,  EasingType.IN_LOG10,       EasingType.IN_OUT_LOG10,
    EasingType.OUT_LOG10,        EasingType.IN_SQRT,          EasingType.IN_OUT_SQRT,    EasingType.OUT_SQRT,       EasingType.SMOOTHSTEP
  ];

  /** the look up table we use for easing curves when zooming in or out */
  public lut: EasingLookupTable                             = new EasingLookupTable();

  /** [INPUT] client function to get the current camera z position */
  public getZ: () => number                                 = () => {return 0;};

  /** [INPUT] client function to get the minimum z position */
  public getNear: () => number                              = () => {return 0;};

  /** [INPUT] client function to get the maximum z position */
  public getFar: () => number                               = () => {return 1000;};

  /** [OUTPUT] client function to actually zoom */
  public zoomDelta: (delta: number, event: WheelEvent) => void = () => {return ;};

  /** [INPUT] client function to test if we are ignoring this event */
  public filtered: (event: WheelEvent) => boolean

  /** the minimum z position for the camera */
  private minZ: number                                        = 0;

  /** the maximum z position for the camera */
  private maxZ: number                                        = 0;

  /** the current camera Z position */
  private pos: number                                         = 0;

  /** the canvas we are watching for mouse wheel events on */

  private canvas: HTMLCanvasElement|null                      = null;

  /**
   * standard constructor, you must provide a callbacks for the Zoomer to know
   * the z extent and how to tell the PanZoom controller to actually zoom in or out.
   *
   * @param canvas the canvas we react to mouse wheel events on
   *
   * @param getZ you must provide a call back that fetches the current camera z position
   *
   * @param getNear you must provide a call back that fetches the minimum z position
   *
   * @param getFar you must provide a call back that fetches the maximum z position
   *
   * @param zoomDelta you must provide a call back that actually does the zooming
   *
   * @param filtered you must provide a call back that indicates when to ignore the mouse wheel
   *
   */
  constructor(canvas: HTMLCanvasElement,
              getZ: () => number,
              getNear: () => number,
              getFar:  () => number,
              zoomDelta: (delta: number, event: WheelEvent) => void,
              filtered: (event: WheelEvent) => boolean) {

    this.getZ      = getZ;
    this.getNear   = getNear;
    this.getFar    = getFar;
    this.zoomDelta = zoomDelta;
    this.filtered  = filtered;

    this.canvas    = canvas;

    this.update();

    Logger.debug(`[Zoomer] constructor -  ready!`);

  }

  /** helper to re-create the exisint easing curve look up table, based on current parameters */
  private createLUT(): void {

    let z: Zoomer       = this;

    Logger.debug(`[Zoomer] createLUT - easing mode: ${z.easing}`);

    z.lut = z.lut
      .setStart(z.getMinimum())
      .setEnd(z.getMaximum())
      .setKind(z.easing)
      .setSteps(200)
      .build();

  }

  /** re-sync any inputs from the main PanZoom control */
  public update(): void {

    let z: Zoomer       = this;

    let oldMinZ: number = z.minZ;
    let oldMaxZ: number = z.maxZ;

    z.minZ              = z.getNear();
    z.maxZ              = z.getFar();

    z.pos               = z.getZ();

    /*
     * if we changed the range of possible Z axis, we have to rebuild our easing
     * curve look up table.
     *
     */

    if ((oldMinZ != z.minZ) || (oldMaxZ != z.maxZ)) {

      z.createLUT();
    }

  }

  /** react to the mouse wheel */
  public scrolled(event: WheelEvent): void {

    /* do nothing, subclass will handle */

  }

  /** if there is any zoom animation happening already, cancel it. */
  public cancel(): void {

    Logger.debug(`[Zoomer] cancel -  stopping animation!`);
  }

  /** fetch the current scrollbar position (on the z axis of the camera) */
  public getPosition(): number {

    return this.pos;
  }

  /** fetch the minimum scroll bar position possible */
  public getMinimum(): number {

    return this.minZ;
  }

  /** fetch the maximum scroll bar position possible */
  public getMaximum(): number {

    return this.maxZ;
  }

  /** fetch the canvas we listen to for mouse wheel events */
  public getCanvas(): HTMLCanvasElement {

    return this.canvas;
  }

  /** force the easing mode */
  public setEasing(easing: EasingType): this {

    this.easing = easing;

    /* update lookup table */

    this.createLUT();

    Logger.debug(`[Zoomer] New Easing mode set: ${this.easing}`);

    return this;
  }

  /** fetch the current easing mode */
  public getEasing(): EasingType {

    return this.easing;
  }

  /** cycle to the next possible easing mode */
  public nextEasingMode(): EasingType {

    let z: Zoomer       = this;

    /* find the position of the current mode */

    let index: number = z.easingModes.indexOf(z.easing);

    if (index < 1) {

      z.easing = EasingType.LINEAR;

      index = z.easingModes.indexOf(z.easing);
    }

    /* wrap around if we need to */

    index++;

    if (index >= z.easingModes.length) {

      index = 0;
    }

    /* change to the new easing mode */

    z.easing = z.easingModes[index];

    z.setEasing(z.easing);

    return z.easing;
  }

  /** cycle to the previous possible easing mode */
  public previousEasingMode(): EasingType {

    let z: Zoomer       = this;

    /* find the position of the current mode */

    let index: number = z.easingModes.indexOf(z.easing);

    if (index < 1) {

      z.easing = EasingType.LINEAR;

      index = z.easingModes.indexOf(z.easing);
    }

    /* wrap around if we need to */

    index--;

    if (index < 0) {

      index = z.easingModes.length - 1;
    }

    /* change to the new easing mode */

    z.easing = z.easingModes[index];

    z.setEasing(z.easing);

    return z.easing;
  }

  /** reset easing to linear mode */
  public resetEasing(): EasingType {

    this.setEasing(EasingType.OUT_QUADRATIC);

    return this.easing;
  }


}
